/* eslint-disable no-alert */
import React, { useState } from 'react';

// Sagas
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import { withTranslation } from 'react-i18next';

import {
  Row, Col, Card, CardBody, CardTitle, Modal,
} from 'reactstrap';
import moment from 'moment';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

import { Can } from '../../utilities/Can';
import BaseGrid from '../../components/Common/BaseGrid';
import { del } from '../../helpers/api_helper';
import TruckForm from '../../components/Sourcing/TruckForm';

function Trucks(props) {
  toastr.options = {
    positionClass: 'toast-top-right',
    newestOnTop: true,
  };

  const { t } = props;

  const [modalOpen, setModalOpen] = useState(false);
  const [signal, setSignal] = useState(0);
  const [editEntity, setEditEntity] = useState({});

  const editTruck = (truck) => {
    setEditEntity(truck);
    setModalOpen(true);
  };

  const deleteTruck = async (truck) => {
    // eslint-disable-next-line no-alert
    // eslint-disable-next-line no-restricted-globals
    const cnfrm = confirm(t(`Are you sure you want to delete ${truck.reference}?`));

    if (cnfrm) {
      try {
        await del(`/api/sourcing/trucks/${truck.id}`);

        toastr.success(`Deleted truck ID: ${truck.id}`, 'Success');
        setSignal(signal + 1);
      } catch (error) {
        toastr.error(error.message, 'Error');
      }
    }
  };

  return (
    <div className="page-content trucks-page">
      <div className="container-fluid">
        <Row>
          <Col xxl="12">
            <Card className="trucks-page__card">
              <CardBody className="trucks-page__body">
                <div className="trucks-page__header">
                  <div>
                    <span className="trucks-page__eyebrow">{props.t('Sourcing')}</span>
                    <CardTitle className="h4 trucks-page__title">{props.t('Trucks')}</CardTitle>
                    <p className="trucks-page__copy mb-0">
                      {props.t('Track inbound and outbound shipments, supplier programs, and receiving status in one place.')}
                    </p>
                  </div>
                </div>

                <Can i="see" this="Sourcing">
                  <div className="trucks-grid">
                    <BaseGrid
                      url="/api/sourcing/trucks"
                      hasSearch
                      shouldUpdate={signal}
                      create={() => { setEditEntity({}); setModalOpen(true); }}
                      config={
                      {
                        rowClass: (ent) => {
                          if (ent.received_at === null
                            && moment(ent.expected_at).isBefore(moment())) {
                            return 'trucks-grid__row trucks-grid__row--overdue';
                          }

                          if (ent.received_at === null) {
                            return 'trucks-grid__row trucks-grid__row--pending';
                          }

                          return 'trucks-grid__row';
                        },
                        columns: {
                          id: {
                            label: 'ID',
                            sort: true,
                            link: {
                              to: '/sourcing/truck/%id%',
                            },
                          },
                          reference: {
                            label: 'Reference',
                            sort: true,
                            link: {
                              to: '/sourcing/truck/%id%',
                            },
                          },
                          outbound: {
                            label: 'In/Out',
                            html: true,
                            format: (field) => {
                              if (field) {
                                return `<span class="badge trucks-grid__status trucks-grid__status--outbound">${t('Outbound')}</span>`;
                              }

                              return `<span class="badge trucks-grid__status trucks-grid__status--inbound">${t('Inbound')}</span>`;
                            },
                          },
                          created_at: {
                            label: 'Created',
                            date: {
                              format: (date) => (moment(date).fromNow()),
                              timezone: process.env.LOCAL_TZ,
                            },
                          },
                          supplier: {
                            label: 'Supplier',
                            format: (field) => (field ? field.name : '--'),
                          },
                          code: {
                            label: 'Code',
                            format: (field, row) => (
                              row.supplier_program ? (`
                                ${row.supplier.programs.find(
                                (x) => x.name === row.supplier_program,
                              ).lot_prefix}
                              `) : '--'
                            ),
                          },
                          supplier_program: {
                            label: 'Program',
                          },
                          pallet_count: {
                            label: 'Pallet Count',
                          },
                          expected_at: {
                            label: 'Expected',
                            date: {
                              format: (date) => (moment.utc(date).fromNow()),
                              time: false,
                            },
                          },
                          received_at: {
                            label: 'Received',
                            date: {
                              format: (date) => (moment(date).fromNow()),
                              timezone: process.env.LOCAL_TZ,
                            },
                          },
                          comments: {
                            label: 'Comments',
                          },
                          actions: {
                            label: props.t('Actions'),
                            actions: [
                              {
                                key: 'edit',
                                icon: 'bx bx-pencil',
                                class: 'btn btn-sm btn-soft-dark',
                                alt: props.t('Edit'),
                                click: editTruck,
                              },
                              {
                                key: 'delete',
                                icon: 'mdi mdi-close-thick',
                                class: 'btn btn-sm btn-soft-danger',
                                alt: props.t('Delete'),
                                click: deleteTruck,
                              },
                            ],
                          },
                        },
                        pagination: {
                          sort: 'id',
                          direction: 'desc',
                          perPage: 10,
                        },
                      }
                      }
                    />
                  </div>
                </Can>

                <Can not i="see" this="Sourcing"><p>{props.t('Unauthorized')}</p></Can>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <Modal
          size="md"
          isOpen={modalOpen}
          className="trucks-page__modal"
        >
          <div className="modal-header">
            <h5
              className="modal-title mt-0"
            >
              {t('Truck Shipment')}
            </h5>
            <button
              onClick={() => setModalOpen(false)}
              type="button"
              className="close"
              data-dismiss="modal"
              aria-label="Close"
            >
              <span aria-hidden="true">&times;</span>
            </button>
          </div>
          <div className="modal-body">
            <TruckForm
              entity={editEntity}
              onSuccess={() => { setModalOpen(false); setSignal(signal + 1); }}
            />
          </div>
          <div className="modal-footer">
            <button
              type="button"
              onClick={() => setModalOpen(false)}
              className="btn btn-soft-dark"
              data-dismiss="modal"
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              form="TruckForm"
              className="btn btn-primary"
            >
              {t('Save')}
            </button>
          </div>
        </Modal>
      </div>
    </div>
  );
}

Trucks.defaultProps = {
  t: (x) => (x),
};

Trucks.propTypes = {
  t: PropTypes.any,
};

export default connect(() => ({

}), {})(withTranslation()(Trucks));
